import React, { useState, useEffect, useRef } from 'react';
import { Send, Zap, User } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${API_BASE}/api`;

const ChatInterface = ({ sessionId, onNewSession }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation history if sessionId provided
  useEffect(() => {
    if (currentSessionId) {
      loadConversationHistory();
    } else {
      // Start with a welcome message
      setMessages([
        {
          role: 'assistant',
          content: `🔥 Hey ${user?.name || 'Champion'}! Ready to CRUSH your goals today? I'm here to help you transform every area of your life. What do you want to work on? 💪`,
          timestamp: new Date()
        }
      ]);
    }
  }, [currentSessionId, user]);

  const loadConversationHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${currentSessionId}`);
      if (response.data.messages) {
        setMessages(response.data.messages);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
      // Start fresh if can't load history
      setMessages([
        {
          role: 'assistant',
          content: `🔥 Welcome back, ${user?.name || 'Champion'}! Let's continue your transformation journey. What's on your mind? 💪`,
          timestamp: new Date()
        }
      ]);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/send`, {
        message: userMessage.content,
        session_id: currentSessionId
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.ai_response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

      // Update session ID if this is a new conversation
      if (!currentSessionId && response.data.conversation.session_id) {
        setCurrentSessionId(response.data.conversation.session_id);
        onNewSession?.(response.data.conversation.session_id);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Add error message
      const errorMessage = {
        role: 'assistant',
        content: "🔧 I'm having trouble connecting right now, but don't let that stop you! Keep pushing forward and try again - YOU'VE GOT THIS! 💪",
        timestamp: new Date(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = async () => {
    try {
      const response = await axios.post(`${API}/chat/new`);
      setCurrentSessionId(response.data.session_id);
      setMessages([
        {
          role: 'assistant',
          content: `🚀 Fresh start! I LOVE the energy, ${user?.name || 'Champion'}! What new challenge are we tackling today? Let's make it HAPPEN! 🔥`,
          timestamp: new Date()
        }
      ]);
      onNewSession?.(response.data.session_id);
    } catch (error) {
      console.error('Failed to start new chat:', error);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-950 to-black rounded-2xl overflow-hidden border border-gray-700">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-sm border-b border-electric-blue/20">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full" style={{background: 'linear-gradient(135deg, #007BFF 0%, #39FF14 100%)'}}></div>
          </div>
          <div>
            <h3 className="text-white font-black text-lg tracking-wide">COACH AI</h3>
            <p className="text-neon-green text-xs font-bold flex items-center">
              <div className="w-2 h-2 rounded-full mr-2 animate-pulse" style={{background: 'linear-gradient(135deg, #007BFF 0%, #39FF14 100%)'}}></div>
              READY TO TRANSFORM
            </p>
          </div>
        </div>
        
        <button
          onClick={startNewChat}
          className="px-4 py-2 bg-gradient-to-r from-electric-blue/20 to-neon-green/20 text-white rounded-full text-sm hover:scale-105 transition-transform border border-electric-blue/30"
        >
          New Chat
        </button>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-transparent via-gray-950/20 to-transparent">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-power`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className={`max-w-xs lg:max-w-md ${message.role === 'user' ? 'order-2' : 'order-1'}`}>
              <div className={`relative p-4 rounded-2xl shadow-lg ${
                message.role === 'user' 
                  ? 'bg-gradient-to-r from-gray-800 to-gray-700 border border-gray-600 rounded-br-md' 
                  : message.isError
                    ? 'bg-gradient-to-r from-red-600/80 to-red-500/80 rounded-bl-md'
                    : 'bg-gradient-to-r from-electric-blue to-neon-green rounded-bl-md'
              }`}>
                {message.role === 'assistant' && !message.isError && (
                  <div className="absolute -inset-1 bg-gradient-to-r from-electric-blue/30 to-neon-green/30 rounded-2xl blur-sm -z-10"></div>
                )}
                
                <p className={`text-white font-medium text-sm leading-relaxed ${
                  message.role === 'assistant' && !message.isError ? 'font-bold' : ''
                }`}>
                  {message.content}
                </p>
                
                <div className={`text-xs mt-2 ${
                  message.role === 'user' ? 'text-gray-300' : 'text-white/80'
                }`}>
                  {new Date(message.timestamp).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>

            {/* Avatar */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              message.role === 'user' ? 'order-1 mr-3 bg-gray-700' : 'order-2 ml-3 bg-gradient-primary'
            }`}>
              {message.role === 'user' ? (
                <User className="w-5 h-5 text-white" />
              ) : (
                <Zap className="w-5 h-5 text-white" />
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start animate-slide-power">
            <div className="ml-11 max-w-xs">
              <div className="bg-gradient-to-r from-electric-blue to-neon-green p-4 rounded-2xl rounded-bl-md">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 bg-gradient-to-r from-gray-900 to-gray-800 border-t border-electric-blue/20">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Share what's on your mind..."
              className="w-full bg-gradient-to-r from-gray-800 to-gray-700 text-white px-4 py-3 pr-12 rounded-full text-sm outline-none border border-gray-600 focus:border-electric-blue/50 transition-all resize-none"
              rows="1"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              disabled={isLoading}
            />
          </div>
          
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110"
            style={{
              background: inputMessage.trim() && !isLoading 
                ? 'linear-gradient(135deg, #007BFF 0%, #39FF14 100%)' 
                : '#374151',
              boxShadow: inputMessage.trim() && !isLoading 
                ? '0 0 20px rgba(57, 255, 20, 0.4)' 
                : 'none'
            }}
          >
            <Send className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;